import { authDevicePayload } from '../auth/deviceId'
import { apiRequest, setAccessToken } from './client'
import type { AuthSession, MeResponse } from './types'

type LoginResponseRaw = {
  access_token: string
  refresh_token: string | null
  redirect_path: string | null
}

type TokenRefreshResponseRaw = {
  access_token: string
  refresh_token: string | null
}

/** Логин по короткому имени достраивается до внутреннего домена — как в market. */
const normalizeLoginEmail = (email: string): string => {
  const trimmed = email.trim().toLowerCase()
  return trimmed.includes('@') ? trimmed : `${trimmed}@provisium.local`
}

export const refreshAccessToken = async (refreshToken: string) => {
  const device = authDevicePayload()
  const response = await apiRequest<TokenRefreshResponseRaw>('/auth/refresh', {
    method: 'POST',
    body: { refresh_token: refreshToken, ...device },
    auth: false,
  })
  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token ?? refreshToken,
  }
}

export const login = async (
  email: string,
  password: string,
  force = false,
): Promise<AuthSession> => {
  const device = authDevicePayload()
  const response = await apiRequest<LoginResponseRaw>('/auth/login', {
    method: 'POST',
    body: {
      email: normalizeLoginEmail(email),
      password,
      ...device,
      ...(force ? { force: true } : {}),
    },
    auth: false,
  })

  setAccessToken(response.access_token)

  const me = await apiRequest<MeResponse>('/me')
  const organization = me.organizations[0]
  if (!organization) {
    throw new Error('У аккаунта нет организации. Обратитесь в поддержку Провизиум.')
  }

  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    organizationId: organization.id,
    email: me.email,
    displayName: me.displayName,
  }
}

type RegisterAccountRequestBody = {
  org_type: 'buyer'
  email: string
  password: string
  first_name: string
  last_name: string
  contact_phone: string
  legal_name: string
  inn: string
  city: string
  trade_name: string
  consent: boolean
  consent_offer: boolean
  marketing_consent?: boolean
  notifications_consent?: boolean
  plan?: 'start'
  horeca_type?: string
}

type RegisterAccountResponseRaw = {
  access_token: string
  refresh_token: string
  organization: { id: number }
}

/** Backend требует значение horeca_type, но UI его не собирает (ресторан по умолчанию). */
const DEFAULT_HORECA_TYPE = 'cafe'

/**
 * Регистрация ресторанного аккаунта. Возвращает готовую сессию (как login):
 * ставит access-токен, тянет /me, выбирает организацию.
 */
export const registerAccount = async (
  request: Omit<RegisterAccountRequestBody, 'org_type' | 'horeca_type' | 'plan'>,
): Promise<AuthSession> => {
  const email = normalizeLoginEmail(request.email)
  const response = await apiRequest<RegisterAccountResponseRaw>('/auth/register', {
    method: 'POST',
    body: {
      ...request,
      email,
      org_type: 'buyer',
      plan: 'start',
      horeca_type: DEFAULT_HORECA_TYPE,
    } satisfies RegisterAccountRequestBody,
    auth: false,
  })

  setAccessToken(response.access_token)
  const me = await apiRequest<MeResponse>('/me')
  const organization =
    me.organizations.find((item) => item.id === response.organization.id) ?? me.organizations[0]
  if (!organization) {
    throw new Error('Организация не создана. Обратитесь в поддержку Провизиум.')
  }

  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    organizationId: organization.id,
    email: me.email,
    displayName: me.displayName,
  }
}

/** Запрос ссылки для сброса пароля на email. */
export const requestPasswordReset = async (email: string) => {
  const normalizedEmail = normalizeLoginEmail(email)
  return apiRequest<{ message?: string }>('/auth/forgot-password', {
    method: 'POST',
    body: { email: normalizedEmail },
    auth: false,
  })
}

/** Подтверждение нового пароля по токену из письма. */
export const confirmPasswordReset = async (token: string, password: string) =>
  apiRequest<{ message?: string }>('/auth/reset-password', {
    method: 'POST',
    body: { token, password },
    auth: false,
  })

export const logout = async (session: AuthSession | null) => {
  const refreshToken = session?.refreshToken
  if (!refreshToken) {
    setAccessToken(null)
    return
  }
  const device = authDevicePayload()
  try {
    await apiRequest<void>('/auth/logout', {
      method: 'POST',
      body: { refresh_token: refreshToken, device_id: device.device_id },
    })
  } catch {
    // Локальную сессию чистим даже если отзыв не удался.
  }
  setAccessToken(null)
}
